import React, { useState } from 'react';
import { useLocation, Link } from "react-router-dom";
import GolfEventLocationController from './GolfEventLocationController';
import GolfEventHeaderNav from './GolfEventHeaderNav';
import GolfLocationDetail from './GolfLocationDetail';
import Spinner from "../../components/atoms/Spinner";

// ============================================================================
// LIVE CARDS (isToday = true)
// ============================================================================

const GrandstandCardLive = ({ location, data, onClick }) => {
  const value = data?.value || 0;
  const maxValue = location.maxValue || 0;
  const percentage = data?.percentage || 0;
  const online = data?.online ?? false;
  const percentDisplay = Math.round(percentage * 100);

  const getCapacityColor = (pct) => {
    if (pct >= 0.9) return '#dc3545';
    if (pct >= 0.7) return '#fd7e14';
    if (pct >= 0.5) return '#ffc107';
    return '#28a745';
  };

  const capacityColor = getCapacityColor(percentage);

  return (
    <div
      onClick={onClick}
      style={{
        borderRadius: "16px",
        padding: "24px",
        background: "#fff",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)",
        cursor: "pointer",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        minWidth: "280px",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.1)';
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={{ fontSize: "18px", fontWeight: "600", color: "#333" }}>{location.name}</div>
        <div style={{
          width: "10px", height: "10px", borderRadius: "50%",
          backgroundColor: online ? "#28a745" : "#dc3545",
        }} />
      </div>

      <div style={{
        width: "100%", height: "8px", backgroundColor: "#e9ecef",
        borderRadius: "4px", marginBottom: "12px", overflow: "hidden"
      }}>
        <div style={{
          width: `${Math.min(percentDisplay, 100)}%`, height: "100%",
          backgroundColor: capacityColor, borderRadius: "4px", transition: "width 0.5s ease"
        }} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <span style={{ fontSize: "28px", fontWeight: "700", color: capacityColor }}>{percentDisplay}%</span>
          <span style={{ fontSize: "14px", color: "#888", marginLeft: "4px" }}>full</span>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{ fontSize: "16px", fontWeight: "500", color: "#555" }}>{value.toLocaleString()}</span>
          <span style={{ fontSize: "13px", color: "#999" }}> / {maxValue.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

const MarketCardLive = ({ location, data, onClick }) => {
  const value = data?.value || 0;
  const maxValue = location.maxValue || 0;
  const percentage = data?.percentage || 0;
  const online = data?.online ?? false;
  const throughputCount = data?.throughputCount || 0;
  const dwellTime = data?.dwellTime;
  const percentDisplay = Math.round(percentage * 100);

  const getCapacityColor = (pct) => {
    if (pct >= 0.9) return '#dc3545';
    if (pct >= 0.7) return '#fd7e14';
    if (pct >= 0.5) return '#ffc107';
    return '#28a745';
  };

  const capacityColor = getCapacityColor(percentage);

  const formatDwellTime = (seconds) => {
    if (!seconds) return '--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div
      onClick={onClick}
      style={{
        borderRadius: "16px",
        padding: "24px",
        background: "#fff",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)",
        cursor: "pointer",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        minWidth: "280px",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.1)';
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={{ fontSize: "18px", fontWeight: "600", color: "#333" }}>{location.name}</div>
        <div style={{
          width: "10px", height: "10px", borderRadius: "50%",
          backgroundColor: online ? "#28a745" : "#dc3545",
        }} />
      </div>

      <div style={{
        width: "100%", height: "8px", backgroundColor: "#e9ecef",
        borderRadius: "4px", marginBottom: "12px", overflow: "hidden"
      }}>
        <div style={{
          width: `${Math.min(percentDisplay, 100)}%`, height: "100%",
          backgroundColor: capacityColor, borderRadius: "4px", transition: "width 0.5s ease"
        }} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "12px" }}>
        <div>
          <span style={{ fontSize: "28px", fontWeight: "700", color: capacityColor }}>{percentDisplay}%</span>
          <span style={{ fontSize: "14px", color: "#888", marginLeft: "4px" }}>full</span>
        </div>
        <div style={{ textAlign: "right" }}>
          <span style={{ fontSize: "16px", fontWeight: "500", color: "#555" }}>{value.toLocaleString()}</span>
          <span style={{ fontSize: "13px", color: "#999" }}> / {maxValue.toLocaleString()}</span>
        </div>
      </div>

      <div style={{
        display: "flex", justifyContent: "space-between",
        paddingTop: "12px", borderTop: "1px solid #eee"
      }}>
        <div>
          <div style={{ fontSize: "12px", color: "#999", marginBottom: "2px" }}>Throughput</div>
          <div style={{ fontSize: "18px", fontWeight: "600", color: "#333" }}>{throughputCount.toLocaleString()}</div>
        </div>
        {dwellTime && (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "12px", color: "#999", marginBottom: "2px" }}>Avg Dwell</div>
            <div style={{ fontSize: "18px", fontWeight: "600", color: "#333" }}>{formatDwellTime(dwellTime.time)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const GateCardLive = ({ location, data, onClick }) => {
  const countOut = data?.countOut || 0;
  const online = data?.online ?? false;

  return (
    <div
      onClick={onClick}
      style={{
        borderRadius: "16px",
        padding: "24px",
        background: "#fff",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)",
        cursor: "pointer",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        minWidth: "280px",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.1)';
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={{ fontSize: "18px", fontWeight: "600", color: "#333" }}>{location.name}</div>
        <div style={{
          width: "10px", height: "10px", borderRadius: "50%",
          backgroundColor: online ? "#28a745" : "#dc3545",
        }} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <span style={{ fontSize: "28px", fontWeight: "700", color: "#333" }}>{countOut.toLocaleString()}</span>
          <span style={{ fontSize: "14px", color: "#888", marginLeft: "6px" }}>total exits</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// HISTORICAL CARDS (isToday = false)
// ============================================================================

const HistoricalCard = ({ location, onClick }) => {
  return (
    <div
      onClick={onClick}
      style={{
        borderRadius: "16px",
        padding: "20px 24px",
        background: "#fff",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)",
        cursor: "pointer",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        minWidth: "280px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.1)';
      }}
    >
      <div>
        <div style={{ fontSize: "18px", fontWeight: "600", color: "#333" }}>
          {location.name}
        </div>
        <div style={{ fontSize: "13px", color: "#999", textTransform: "capitalize", marginTop: "2px" }}>
          {location.type}
        </div>
      </div>
      <div style={{ fontSize: "14px", color: "#007bff" }}>
        View Data &rarr;
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function GolfEventLocation() {
  const location = useLocation();
  const locationData = location.state?.currentLocation;
  const locationId = location.state?.id;

  // Search and filter
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  const filterLocations = (locations) => {
    if (!searchTerm) return locations;
    return locations.filter(loc =>
      loc.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <GolfEventLocationController locationData={locationData} locationId={locationId}>
      {({
        loading,
        locations,
        grandstands,
        markets,
        gates,
        hospitalityVenues,
        currentData,
        selectedLocation,
        detailDailyData,
        detailLoading,
        openLocationDetail,
        closeLocationDetail,
        isToday,
        isFutureDate,
        autoFetchEnabled,
        setAutoFetchEnabled,
        refreshData,
        authCtx,
        selectedDate,
        setSelectedDate,
        minDate,
        maxDate,
        enabledDates,
        showCalendar,
        setShowCalendar
      }) => {

        const filteredGrandstands = filterLocations(grandstands);
        const filteredMarkets = filterLocations(markets);
        const filteredGates = filterLocations(gates);

        const showGrandstands = (filterType === "all" || filterType === "grandstand") && filteredGrandstands.length > 0;
        const showMarkets = (filterType === "all" || filterType === "market") && filteredMarkets.length > 0;
        const showGates = (filterType === "all" || filterType === "gate") && filteredGates.length > 0;

        return (
          <>
            {authCtx.loggedIn ? (
              <>
                {/* Detail Modal */}
                {selectedLocation && (
                  <GolfLocationDetail
                    location={selectedLocation}
                    locationData={locationData}
                    currentData={isToday ? currentData[selectedLocation.locationId] : null}
                    dailyData={detailDailyData}
                    detailLoading={detailLoading}
                    isToday={isToday}
                    onClose={closeLocationDetail}
                    authCtx={authCtx}
                  />
                )}

                {loading ? (
                  <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100vw',
                    height: '100vh'
                  }}>
                    <Spinner size="large" />
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div className="sticky-top pt-3" style={{ backgroundColor: '#ffffff' }}>
                      <div className="px-3">
                        <GolfEventHeaderNav
                          locationData={locationData}
                          loading={loading}
                          isToday={isToday}
                          onRefresh={refreshData}
                          selectedDate={selectedDate}
                          setSelectedDate={setSelectedDate}
                          minDate={minDate}
                          maxDate={maxDate}
                          enabledDates={enabledDates}
                          showCalendar={showCalendar}
                          setShowCalendar={setShowCalendar}
                          searchTerm={searchTerm}
                          setSearchTerm={setSearchTerm}
                          filterType={filterType}
                          setFilterType={setFilterType}
                        />
                      </div>
                      <hr />
                    </div>

                    <div className="container-fluid" style={{ padding: "20px" }}>

                      {/* ========== LIVE VIEW ========== */}
                      {isToday ? (
                        <>
                          {showGrandstands && (
                            <div style={{ marginBottom: "32px" }}>
                              <div style={{ fontSize: "1.4rem", fontWeight: "600", marginBottom: "16px", color: "#333" }}>
                                Grandstands
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                gap: '20px'
                              }}>
                                {filteredGrandstands.map(loc => (
                                  <GrandstandCardLive
                                    key={loc.locationId}
                                    location={loc}
                                    data={currentData[loc.locationId]}
                                    onClick={() => openLocationDetail(loc)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {showMarkets && (
                            <div style={{ marginBottom: "32px" }}>
                              {showGrandstands && <hr style={{ marginBottom: "24px" }} />}
                              <div style={{ fontSize: "1.4rem", fontWeight: "600", marginBottom: "16px", color: "#333" }}>
                                Markets
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                                gap: '20px'
                              }}>
                                {filteredMarkets.map(loc => (
                                  <MarketCardLive
                                    key={loc.locationId}
                                    location={loc}
                                    data={currentData[loc.locationId]}
                                    onClick={() => openLocationDetail(loc)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {showGates && (
                            <div style={{ marginBottom: "32px" }}>
                              {(showGrandstands || showMarkets) && <hr style={{ marginBottom: "24px" }} />}
                              <div style={{ fontSize: "1.4rem", fontWeight: "600", marginBottom: "16px", color: "#333" }}>
                                Gates
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                gap: '20px'
                              }}>
                                {filteredGates.map(loc => (
                                  <GateCardLive
                                    key={loc.locationId}
                                    location={loc}
                                    data={currentData[loc.locationId]}
                                    onClick={() => openLocationDetail(loc)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : isFutureDate ? (
                        /* ========== FUTURE DATE VIEW ========== */
                        <div style={{ textAlign: "center", color: "#888", marginTop: "60px", fontSize: "16px" }}>
                          No data available yet for this date
                        </div>
                      ) : (
                        /* ========== HISTORICAL VIEW ========== */
                        <>
                          {showGrandstands && (
                            <div style={{ marginBottom: "32px" }}>
                              <div style={{ fontSize: "1.4rem", fontWeight: "600", marginBottom: "16px", color: "#333" }}>
                                Grandstands
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                gap: '20px'
                              }}>
                                {filteredGrandstands.map(loc => (
                                  <HistoricalCard
                                    key={loc.locationId}
                                    location={loc}
                                    onClick={() => openLocationDetail(loc)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {showMarkets && (
                            <div style={{ marginBottom: "32px" }}>
                              {showGrandstands && <hr style={{ marginBottom: "24px" }} />}
                              <div style={{ fontSize: "1.4rem", fontWeight: "600", marginBottom: "16px", color: "#333" }}>
                                Markets
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                gap: '20px'
                              }}>
                                {filteredMarkets.map(loc => (
                                  <HistoricalCard
                                    key={loc.locationId}
                                    location={loc}
                                    onClick={() => openLocationDetail(loc)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {showGates && (
                            <div style={{ marginBottom: "32px" }}>
                              {(showGrandstands || showMarkets) && <hr style={{ marginBottom: "24px" }} />}
                              <div style={{ fontSize: "1.4rem", fontWeight: "600", marginBottom: "16px", color: "#333" }}>
                                Gates
                              </div>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                                gap: '20px'
                              }}>
                                {filteredGates.map(loc => (
                                  <HistoricalCard
                                    key={loc.locationId}
                                    location={loc}
                                    onClick={() => openLocationDetail(loc)}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* No results */}
                      {!isFutureDate && !showGrandstands && !showMarkets && !showGates && (
                        <div style={{ textAlign: "center", color: "#888", marginTop: "60px", fontSize: "16px" }}>
                          No locations match your search
                        </div>
                      )}

                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="p-4">
                <Link to={"/auth"}>
                  Not Authorized. Please Log-In with given credentials or contact support.
                </Link>
              </div>
            )}
          </>
        );
      }}
    </GolfEventLocationController>
  );
}